import React, { FunctionComponent, PropsWithChildren, useCallback, useContext, useState } from 'react'
import { CrowdSurfConfig, CrowdSurfConfigContext } from './appconfig'
import { teGetCsrfToken } from './fed_api_gateway_csrf_helper'
import * as T from 'fp-ts/es6/Task'
import * as TE from 'fp-ts/es6/TaskEither'
import { pipe } from 'fp-ts/es6/function'
import { teGetUserInfo } from './user_info_helper'

type UnresolvedIdentity = {
  __typename: 'UnresolvedIdentity'
}

type FailedIdentity = {
  __typename: 'FailedIdentity'
}

export type CompletedIdentity = {
  __typename: 'CompletedIdentity'
  data: {
    isLoggedIn: boolean
    userEmail: string
  }
}

interface InitializeHookState {
  data: UnresolvedIdentity | CompletedIdentity | FailedIdentity
  loading: boolean
}

export interface CrowdSurfIdentity {
  data: UnresolvedIdentity | CompletedIdentity | FailedIdentity
  grab: () => void
}

const UnauthenticatedError = {
  name: 'UserUnauthenticatedError',
  message: 'User is not authenticated',
}

export const CrowdSurfIdentityContext = React.createContext<CrowdSurfIdentity>({ data: { __typename: 'UnresolvedIdentity' }, grab: () => undefined })

export const CrowdSurfIdentityContextProvider: FunctionComponent<PropsWithChildren<{}>> = (props: PropsWithChildren<{}>) => {
  const appConfig = useContext<CrowdSurfConfig>(CrowdSurfConfigContext)
  const [state, setState] = useState<InitializeHookState>({
    data: { __typename: 'UnresolvedIdentity' },
    loading: false,
  })

  const grab = useCallback(() => {
    setState({ data: { __typename: 'UnresolvedIdentity' }, loading: true })

    pipe(
      TE.bindTo('config')(TE.right(appConfig)),
      TE.bind('csrfToken', ({ config }) =>
        pipe(
          teGetCsrfToken(config),
          TE.mapLeft(e => {
            if (e === 'UnauthenticatedCSRFFetchError') {
              return UnauthenticatedError
            } else {
              return new Error('Failed to fetch CSRF token due to unknown error')
            }
          }),
        ),
      ),
      TE.bind('userInfo', ({ config, csrfToken }) =>
        pipe(
          teGetUserInfo(config, csrfToken),
          TE.mapLeft(e => {
            if (e === 'UnauthenticatedUserFetchError') {
              return UnauthenticatedError
            } else {
              return new Error('Failed to fetch WhoAmI due to unknown error')
            }
          }),
        ),
      ),
      TE.fold(
        e => {
          setState({
            data: { __typename: 'FailedIdentity' },
            loading: false,
          } as InitializeHookState)

          return T.of(null)
        },
        ({ userInfo }) => {
          setState({
            data: {
              __typename: 'CompletedIdentity',
              data: {
                isLoggedIn: true,
                userEmail: userInfo.userEmail,
              },
            },
            loading: false,
          } as InitializeHookState)
          return T.of(null)
        },
      ),
    )()
  }, [appConfig])

  const identity: CrowdSurfIdentity = {
    data: state.data,
    grab: grab,
  }

  return <CrowdSurfIdentityContext.Provider value={identity}>{props.children}</CrowdSurfIdentityContext.Provider>
}
